import { Calendar, CalendarProps } from 'primereact/calendar';
import { FormEvent } from 'primereact/ts-helpers';
import { classNames } from 'primereact/utils';
import {
  FieldValues,
  useController,
  UseControllerProps,
  UseFormClearErrors,
  UseFormTrigger,
} from 'react-hook-form';

import ErrorMessage from '../../error-message';
import styles from './DatePicker.module.css';

export type DatePickerProps<T extends FieldValues> = Omit<
  CalendarProps,
  'name' | 'value' | 'onChange'
> & {
  label?: string;
  clearErrors?: UseFormClearErrors<T>;
  showError?: boolean;
  trigger?: UseFormTrigger<T>;
  endDateAsLastDayOfYear?: boolean;
} & UseControllerProps<T>;

const DatePicker = <T extends FieldValues>({
  name,
  control,
  rules,
  defaultValue,
  label,
  className,
  clearErrors,
  showError = true,
  trigger,
  endDateAsLastDayOfYear,
  selectionMode,
  ...props
}: DatePickerProps<T>) => {
  const {
    field: { value, ref, onChange, ...inputProps },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const handleOnChange = (e: FormEvent<(Date | null)[] | Date>) => {
    const selecetedDates = e.value;

    // Convert the end date to the last day of the year
    // This can happen only when the selection is a range
    if (endDateAsLastDayOfYear && selectionMode === 'range') {
      const endDate = (selecetedDates as Date[])[1];

      if (endDate) {
        endDate.setMonth(11);
        endDate.setDate(31);
      }
    }

    onChange(e);
    clearErrors?.(name);
    trigger?.(name);
  };

  return (
    <div className='field p-fluid'>
      <span className='p-float-label'>
        <Calendar
          id={name}
          value={value}
          ref={ref}
          onChange={handleOnChange}
          {...props}
          {...inputProps}
          className={classNames(className, {
            'p-invalid': error,
          })}
          showIcon
          readOnlyInput
          selectionMode={selectionMode}
        />
        {label && <label htmlFor={name}>{label}</label>}
      </span>
      {showError && (
        <div className={classNames(styles['error-message-box'])}>
          {invalid && (
            <ErrorMessage
              message={Array.isArray(error) ? error[1]?.message : error?.message || ''}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
